import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { conditionalSocket } from "../../types";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

export const IDENTIFY_LOCATION_MODEL_TIERS = [
  "fast",
  "balanced",
  "smart",
] as const;
export type IdentifyLocationModelTier =
  (typeof IDENTIFY_LOCATION_MODEL_TIERS)[number];

export const IDENTIFY_LOCATION_ROLE_FILTERS = [
  "any",
  "ship_to",
  "bill_to",
  "remit_to",
] as const;
export type IdentifyLocationRoleFilter =
  (typeof IDENTIFY_LOCATION_ROLE_FILTERS)[number];

/** Odd sample counts avoid first-place-vote ties across the ensemble. */
export const IDENTIFY_LOCATION_ENSEMBLE_SIZES = [1, 3, 5, 7] as const;

/**
 * Identify Location Step
 *
 * Resolves a free-text location/address description to a `core_locations`
 * record belonging to a company's customer/supplier. Pure LLM
 * paginate-and-reduce — no embeddings or vector store:
 *
 *   1. Load candidate locations (with addresses) for the resolved
 *      customer/supplier ids.
 *   2. If they exceed one prompt page, a parallel map round shortlists the
 *      best matches per page (fast tier), then shortlists are merged.
 *   3. An ensemble of N independent samples each ranks its own top-K from
 *      the final candidate set; votes are aggregated per location into a
 *      confidence score and a merged top-K ranking.
 *
 * Inputs: at least one of `company` / `customer` / `supplier` must be wired.
 * A company ref pools locations across all of its customers and suppliers in
 * the organization (Customer/Supplier both FK to co_companies).
 *
 * The full ensemble/vote breakdown is emitted on `ensemble_details` (and
 * therefore persisted on the StepRun's outputData) and summarized into an
 * `ai_reasoning_audit` row for SOX-style traceability.
 */
export const identifyLocationStep = defineStepConfig({
  type: "identify_location",
  name: "Identify Location",
  description:
    "Identify the best-matching location for a company/customer/supplier from a free-text address using an ensemble of AI rankings",
  category: "AI & Agents",

  experimental: {
    reason:
      "Identify Location is under active development and not yet validated for production workflows.",
  },

  inputSchema: z.object({
    company: entityOfType(SystemEntity.CORE_COMPANY)
      .optional()
      .describe(
        "Company whose customer + supplier locations are pooled as candidates.",
      ),
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .optional()
      .describe("Customer whose locations are searched."),
    supplier: entityOfType(SystemEntity.AP_SUPPLIER)
      .optional()
      .describe("Supplier whose locations are searched."),
    locationText: z
      .string()
      .min(1)
      .describe(
        "Free-text location/address description to identify (e.g. a ship-to block extracted from a document).",
      ),
    additionalContext: z
      .string()
      .optional()
      .describe(
        "Optional free-text context to help disambiguation (e.g. email body, order metadata).",
      ),
  }),

  outputSchema: z.object({
    top_pick: conditionalSocket(
      z.object({
        locationId: z.uuid(),
        locationName: z.string(),
        confidence: z.number(),
        reasoning: z.string().nullable(),
      }),
    ).describe(
      "Best match when confidence clears minConfidence; SOCKET_NOT_TAKEN otherwise so downstream branches skip.",
    ),
    matched_location_id: z
      .string()
      .nullable()
      .describe(
        "Flat eval field: the surfaced core_locations id, or null when no match cleared the confidence floor.",
      ),
    matched_location_name: z.string().nullable(),
    confidence: z
      .number()
      .nullable()
      .describe(
        "Ensemble confidence 0–1: blend of first-place agreement share and the winner's mean model score.",
      ),
    top_candidates: z
      .array(
        z.object({
          locationId: z.string(),
          locationName: z.string(),
          address: z.string().nullable(),
          score: z.number(),
          firstPlaceVotes: z.number(),
        }),
      )
      .describe("Merged top-K ranking across all ensemble samples."),
    reasoning: z.string().nullable(),
    ensemble_details: z
      .unknown()
      .describe(
        "Full voting breakdown (per-location votes, sample counts, pagination stats) for audit/debugging.",
      ),
  }),

  configSchema: z.object({
    ensembleSize: z
      .number()
      .int()
      .min(1)
      .max(7)
      .default(3)
      .describe(
        "Number of independent ranking samples to run in parallel. Odd counts avoid first-place ties.",
      ),
    topK: z
      .number()
      .int()
      .min(1)
      .max(10)
      .default(5)
      .describe(
        "How many ranked candidates each sample returns and the size of the merged top_candidates output.",
      ),
    minConfidence: z
      .number()
      .min(0)
      .max(1)
      .default(0.5)
      .describe(
        "Minimum ensemble confidence required to surface a match. Below this, top_pick uses the not-taken sentinel and matched_location_id is null.",
      ),
    modelTier: z
      .enum(IDENTIFY_LOCATION_MODEL_TIERS)
      .default("fast")
      .describe(
        "Model tier for the ensemble ranking round. The pagination shortlist round always uses the fast tier.",
      ),
    roleFilter: z
      .enum(IDENTIFY_LOCATION_ROLE_FILTERS)
      .default("any")
      .describe(
        "Restrict candidates by location capability flag (canShipTo / canBillTo / canRemitTo).",
      ),
    includeDisabled: z
      .boolean()
      .default(false)
      .describe("When true, disabled locations are included as candidates."),
    includeOrganizationLocations: z
      .boolean()
      .default(false)
      .describe(
        "When true, locations owned directly by the organization (linked to no customer or supplier) are also included as candidates.",
      ),
    additionalInstructions: z
      .string()
      .max(10_000)
      .optional()
      .describe(
        "Extra instructions appended to the ranking prompts (e.g. tie-breaking rules specific to this workflow).",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },

  /**
   * Eval config — drives the Test Suite tab on the node. Scoring is on
   * `matched_location_id` (null is a valid expected value: "correctly
   * identified that nothing matches above the confidence floor"). Context
   * fields let the test-suite UI show why a pick happened next to the id.
   */
  evalConfig: {
    evalOutputKeys: ["matched_location_id"],
    outputFormat: "object",
    contextOutputKeys: [
      "matched_location_name",
      "confidence",
      "reasoning",
      "top_candidates",
    ],
  },
});
